import Service, { inject as service } from '@ember/service';
import { alias, equal } from '@ember/object/computed';
import DS from 'ember-data';
import SsoConfiguration, { IdentityProvider } from 'cio/models/sso-configuration';

import OAuthService from 'cio/services/oauth';
import ApiService from 'cio/services/api';
import AccountService from 'cio/services/account';
import FeaturesService from 'ember-feature-flags/services/features';
import { Provider } from 'cio/utils/oauth';
import { tracked } from '@glimmer/tracking';
import AuthService from './auth';

export default class Sso extends Service {
  @service account!: AccountService;
  @service api!: ApiService;
  @service store!: DS.Store;
  @service features!: FeaturesService;
  @service oauth!: OAuthService;
  @service auth!: AuthService;

  @tracked ssoConfiguration: SsoConfiguration | null = null;

  @alias('account.current.ssoEnabled') isEnabled!: boolean;
  @equal('ssoConfiguration.identityProvider', IdentityProvider.GoogleOIDC) isGoogleEnabled!: boolean;
  @equal('ssoConfiguration.identityProvider', IdentityProvider.OktaOIDC) isOktaEnabled!: boolean;
  @equal('ssoConfiguration.identityProvider', IdentityProvider.AzureOIDC) isAzureEnabled!: boolean;
  @equal('ssoConfiguration.identityProvider', IdentityProvider.GenericOIDC) isGenericEnabled!: boolean;

  async initialize() {
    if (this.ssoConfiguration != null) {
      return;
    }

    let ssoConfiguration!: SsoConfiguration;
    try {
      ssoConfiguration = await this.store.findRecord('sso-configuration', 'current');
    } catch (err) {
      // ignore 404 errors: it simply means this account doesn't have SSO enabled, which is fine
      if (err.errors?.[0]?.status !== '404') {
        throw err;
      }
    }

    if (ssoConfiguration != null) {
      this.ssoConfiguration = ssoConfiguration;
    }
  }

  // "fetchSSOInfo" hits special unauthorized endpoint to fetch information needed by UI to init SSO login
  // not setting "ssoConfiguration" property of this service on purpose: it's a limited record that shouldn't be referenced outside of login views
  async fetchSSOInfo({ email }: { email: string }) {
    let data = { email };

    let response: { sso_configuration: unknown; user_external_identity_email: string | null } = await this.api.request(
      `login_sso_info`,
      {
        data,
        pathScope: 'global',
        ignoreErrors: true,
      },
    );

    this.store.pushPayload({ sso_configuration: response.sso_configuration });
    let ssoConfiguration = this.store.peekAll('sso-configuration').firstObject;

    return { ssoConfiguration, userExternalIdentityEmail: response.user_external_identity_email };
  }

  async setupGoogleOIDC(ssoConfiguration: SsoConfiguration) {
    // "*" hosted domain parameter allows selecting Google accounts under any custom G Suite domain, e.g. @customer.io (but not public accounts like foo@gmail.com).
    let { authorizationCode, redirectUri } = await this.oauth.open(Provider.GoogleOIDC, { hostedDomain: '*' });

    ssoConfiguration.setProperties({
      identityProvider: IdentityProvider.GoogleOIDC,
      code: authorizationCode,
      redirectUri,
    });

    await ssoConfiguration.save();
    this.ssoConfiguration = ssoConfiguration;

    await this.store.queryRecord('account', {}); // reload account record to update "sso_setting" flag value

    this.auth.invalidateExpiration(); // Remove expiration check if logged with user-password

    return ssoConfiguration;
  }

  async setupOktaOIDC(ssoConfiguration: SsoConfiguration) {
    let { authorizationURL } = await ssoConfiguration.getOpenIDConfigurationInfo();

    let { authorizationCode, redirectUri } = await this.oauth.open(Provider.OktaOIDC, {
      authorizationURL,
      clientID: ssoConfiguration.oidcClientId,
    });

    ssoConfiguration.setProperties({
      identityProvider: IdentityProvider.OktaOIDC,
      code: authorizationCode,
      redirectUri,
    });

    await ssoConfiguration.save();
    this.ssoConfiguration = ssoConfiguration;

    await this.store.queryRecord('account', {}); // reload account record to update "sso_setting" flag value

    this.auth.invalidateExpiration(); // Remove expiration check if logged with user-password

    return ssoConfiguration;
  }

  async setupAzureOIDC(ssoConfiguration: SsoConfiguration) {
    let { authorizationURL } = await ssoConfiguration.getOpenIDConfigurationInfo();

    let { authorizationCode, redirectUri } = await this.oauth.open(Provider.AzureOIDC, {
      authorizationURL,
      clientID: ssoConfiguration.oidcClientId,
    });

    ssoConfiguration.setProperties({
      identityProvider: IdentityProvider.AzureOIDC,
      code: authorizationCode,
      redirectUri,
    });

    await ssoConfiguration.save();
    this.ssoConfiguration = ssoConfiguration;

    await this.store.queryRecord('account', {}); // reload account record to update "sso_setting" flag value

    this.auth.invalidateExpiration(); // Remove expiration check if logged with user-password

    return ssoConfiguration;
  }

  async setupGenericOIDC(ssoConfiguration: SsoConfiguration) {
    let { authorizationURL } = await ssoConfiguration.getOpenIDConfigurationInfo();

    let { authorizationCode, redirectUri } = await this.oauth.open(Provider.GenericOIDC, {
      authorizationURL,
      clientID: ssoConfiguration.oidcClientId,
    });

    ssoConfiguration.setProperties({
      identityProvider: IdentityProvider.GenericOIDC,
      code: authorizationCode,
      redirectUri,
    });

    await ssoConfiguration.save();
    this.ssoConfiguration = ssoConfiguration;

    await this.store.queryRecord('account', {}); // reload account record to update "sso_setting" flag value

    this.auth.invalidateExpiration(); // Remove expiration check if logged with user-password

    return ssoConfiguration;
  }

  async disableSSO() {
    if (this.ssoConfiguration == null) {
      return;
    }

    await this.ssoConfiguration.destroyRecord();

    await this.store.queryRecord('account', {}); // reload account record to update "sso_setting" flag value

    this.ssoConfiguration = null;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    sso: Sso;
  }
}
